import { Button, useMediaQuery } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import { SideActionItem } from '../../../components/SideActionsList';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { StatusIcon } from '../../../utils/icon';
import { OperatorTemplateContext } from './types';
import { Operator } from '../../../types/operator';
import OperatorStateToggleDialog from './OperatorStateToggleDialog';
import OperatorBadge from './OperatorBadge';

const OperatorTemplate = () => {
  const { t } = useLanguage();
  const { operatorId: operatorIdQueryParam } = useParams();
  const { getOperatorById } = useIotsApi();
  const [displayeOperatorName, setDisplayedOperatorName] = useState<string>('');
  const isMdUp = useMediaQuery('(min-width:768px)');

  const [toggleStateOperator, setToggleStateOperator] =
    useState<Operator | null>(null);

  const operatorId = useMemo(() => {
    return typeof operatorIdQueryParam !== 'string' ||
      operatorIdQueryParam.toLocaleLowerCase() === 'create'
      ? null
      : operatorIdQueryParam;
  }, [operatorIdQueryParam]);

  const {
    data: operator = null,
    isPending,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: ['operator', operatorId ?? ''],
    queryFn: () =>
      operatorId ? getOperatorById(operatorId) : Promise.resolve(null),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useQueryErrorNotifier([error]);

  const queryClient = useQueryClient();

  const contextValue = useMemo<OperatorTemplateContext>(
    () => ({
      operatorId,
      operator,
      refetch: () => void refetch(),
      updateContextOperator: updatedOperator =>
        queryClient.setQueryData<Operator>(
          ['operator', operatorId ?? ''],
          updatedOperator,
        ),
      setDisplayedOperatorName,
      isPending,
      isFetching,
    }),
    [operator, isPending, isFetching, refetch, queryClient, operatorId],
  );

  const sidePanelActions = useMemo<SideActionItem[]>(() => {
    return [
      {
        id: 'information',
        label: t('content.information'),
        statusIcon: operator ? StatusIcon.Completed : StatusIcon.Pending,
        url: `/admin/operators/${operatorId ?? 'create'}`,
        isExactUrl: true,
      },
    ];
  }, [t, operator, operatorId]);

  const isUsingTheNetwork = isPending || isFetching;

  return (
    <LeftPanelSectionTemplate
      title={
        operator ? (
          <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
            {operator.name}
            <OperatorBadge isActive={operator.isActive} small={!isMdUp} />
          </span>
        ) : (
          displayeOperatorName.trim() || t('content.untitled')
        )
      }
      isLoading={isUsingTheNetwork}
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-operators')}
      goBackLinkUrl="/admin/operators"
      actions={
        operator && (
          <>
            <Button
              className="mr-auto"
              color={operator.isActive ? 'error' : undefined}
              variant="outlined"
              type="button"
              onClick={() => {
                setToggleStateOperator(operator);
              }}
              disabled={isUsingTheNetwork}
            >
              {t(`content.${operator.isActive ? 'deactivate' : 'activate'}`)}
            </Button>
            <OperatorStateToggleDialog
              operator={toggleStateOperator}
              onClose={() => setToggleStateOperator(null)}
              onConfirm={updatedOperator => {
                contextValue.updateContextOperator({
                  ...updatedOperator,
                  ...operator,
                });
                contextValue.refetch();
              }}
            />
          </>
        )
      }
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default OperatorTemplate;

export const OperatorSubPageWrapper = () => (
  <OutletContextReliantRoute<OperatorTemplateContext>
    getOutletContextState={({ operator, operatorId, isPending }) =>
      // If the operator has been deleted, it should not be accessible
      operator ? 'ready' : operatorId && isPending ? 'pending' : 'error'
    }
    errorRedirectionPath="/admin/operators"
  />
);
